import "./style/scroll-view.scss"

import React, { ReactNode, useEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import classNames from "classnames"

import { scrollPositionChange } from "@app/store/app/appActions"
import { RootState } from "@app/types"
import { GestureDirection, gestureThreshold, getGestureDirection } from "./gestureDirection"

export interface ScrollViewProps {
    allowOverscrollBottom?: boolean
    allowOverscrollTop?: boolean
    children?: ReactNode
    className?: string
    id?: string
    onScroll?: (e: React.UIEvent<HTMLDivElement>) => void
    onScrollOverflowBottom?: (overflow: number, e: React.TouchEvent<HTMLDivElement>) => void
    onScrollOverflowTop?: (overflow: number, e: React.TouchEvent<HTMLDivElement>) => void
    showScrollBar?: boolean
    transparent?: boolean
}

const ScrollView = (
    {
        allowOverscrollBottom = true,
        allowOverscrollTop = true,
        children,
        className,
        id,
        onScroll = () => undefined,
        onScrollOverflowBottom = () => undefined,
        onScrollOverflowTop = () => undefined,
        showScrollBar = false,
        transparent = false,
    }: ScrollViewProps,
    ref: React.LegacyRef<HTMLDivElement>,
) => {
    const dispatch = useDispatch<any>()
    const scrollPosition = useSelector((state: RootState) => state.app.scrollPosition)
    const innerRef = useRef<HTMLDivElement>({} as any)
    const touchStartX = useRef(0)
    const touchStartY = useRef(0)
    const touchStartedAtTop = useRef(true)
    const gestureDirection = useRef<GestureDirection | null>(null)

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        touchStartX.current = e.touches[0].clientX
        touchStartY.current = e.touches[0].clientY
        touchStartedAtTop.current = innerRef.current.scrollTop <= 15
        gestureDirection.current = null
    }

    const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = innerRef.current

        const dx = e.touches[0].clientX - touchStartX.current
        let dy = e.touches[0].clientY - touchStartY.current

        if (!gestureDirection.current) {
            gestureDirection.current = getGestureDirection(dx, dy)
        }

        if (gestureDirection.current === "horizontal") {
            return
        }

        const atTop = scrollTop <= 15
        const atBottom = scrollHeight - scrollTop <= clientHeight

        if (atTop && !touchStartedAtTop.current) {
            touchStartY.current = e.touches[0].clientY
            touchStartedAtTop.current = true
            dy = 0
        } else if (!atTop) {
            touchStartedAtTop.current = false
        }

        const goingDown = dy < -gestureThreshold
        const goingUp = dy > gestureThreshold

        if (atBottom && goingDown) {
            onScrollOverflowBottom(Math.abs(dy), e)
        }

        if (atTop && goingUp) {
            onScrollOverflowTop(Math.abs(dy), e)

            if (!scrollPosition.top) {
                dispatch(scrollPositionChange(true, false, false))
            }
        }

        if (goingDown && !scrollPosition.goingDown && !atTop) {
            dispatch(scrollPositionChange(false, false, true))
        }

        if (!allowOverscrollBottom && atBottom && goingDown) {
            e.preventDefault()
        }

        if (!allowOverscrollTop && atTop && goingUp) {
            e.preventDefault()
        }
    }

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop } = innerRef.current as unknown as any
        const goingDown = scrollTop > 10
        const atTop = scrollTop <= 15

        onScroll(e)

        if (atTop) {
            if (!scrollPosition.top) {
                dispatch(scrollPositionChange(true, false, false))
            }
        }

        if (goingDown && !scrollPosition.goingDown && !atTop) {
            dispatch(scrollPositionChange(false, false, true))
        }
    }

    useEffect(() => {
        // Reset scroll position on unmount
        return () => {
            dispatch(scrollPositionChange(true, false, false))
        }
    }, [dispatch])

    return (
        <div
            className={classNames("app-scroll-view ", className, {
                "app-scroll-view--transparent": transparent,
            })}
            ref={ref}
        >
            <div
                className={`app-scroll-view__inner ${
                    showScrollBar ? "app-scroll-view__inner--scrollbar" : ""
                }`}
                id={id}
                onScroll={handleScroll}
                onTouchMove={handleTouchMove}
                onTouchStart={handleTouchStart}
                ref={innerRef}
            >
                {children}
            </div>
        </div>
    )
}
export default React.forwardRef(ScrollView)
